"use client";

import { Loader2, Mic, MicOff, ChevronDown } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "@/components/ui/button";
import MicrophoneSelector from "./MicrophoneSelector";
import { useAssembly } from "@/context/SimpleVoiceInput";
import { useMicrophone } from "@/context/MicrophoneAssembly";
import { isMicrophoneBusyError } from "@/lib/microphone/start-coalescing";
import { useToast } from "@/components/ui/use-toast";

const TIMEOUTS = {
  ASSEMBLY_CLEAR_MS: 1000,
  QUEUE_EMPTY_DEFAULT_MS: 800,
  CONNECTION_READY_MS: 8000,
  MIC_OPEN_MS: 4000,
  MIC_CLOSED_MS: 4000,
  MIC_NAME_MS: 1500,
  // Stop finalization timing
  PRE_STOP_QUEUE_DRAIN_DELAY_MS: 150,
  PRE_STOP_QUEUE_DRAIN_TIMEOUT_MS: 600,
  POST_STOP_QUEUE_DRAIN_TIMEOUT_MS: 900,
  POST_FORCE_ENDPOINT_WAIT_MS: 900,
};

const MICROPHONE_STORAGE_KEY = "selectedMicrophoneDeviceId";

const VoiceCommentInput = ({
  onTranscriptionComplete,
  onPartialTranscription,
  disabled = false,
  compact = false,
  showDropdownIndicator = false,
  hideMicSelector = false,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isSwitchingMic, setIsSwitchingMic] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [partialText, setPartialText] = useState("");
  const [accumulatedTranscript, setAccumulatedTranscript] = useState("");
  const [manualConnectPending, setManualConnectPending] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isBrowserSupported, setIsBrowserSupported] = useState(true);
  const [unsupportedReason, setUnsupportedReason] = useState("");
  const [selectedMicDeviceId, setSelectedMicDeviceId] = useState(() => {
    // Load from localStorage on mount
    if (typeof window !== "undefined") {
      return localStorage.getItem("selectedMicrophoneDeviceId") || null;
    }
    return null;
  });
  const onTranscriptionCompleteRef = useRef(onTranscriptionComplete);
  const onPartialTranscriptionRef = useRef(onPartialTranscription);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastProcessedTranscriptRef = useRef("");
  const stopPromiseRef = useRef(null);
  const processedAudioChunkCountRef = useRef(0);
  const { toast } = useToast();

  const {
    connect,
    disconnect,
    sendAudio,
    forceEndpoint,
    connecting,
    connectionReady,
    transcript: assemblyTranscript,
    partialTranscript,
    error: assemblyError,
    reset: resetAssembly,
  } = useAssembly();

  const connectionReadyRef = useRef(connectionReady);
  const assemblyTranscriptRef = useRef(assemblyTranscript);
  const partialTranscriptRef = useRef(partialTranscript);

  useEffect(() => {
    connectionReadyRef.current = connectionReady;
  }, [connectionReady]);

  useEffect(() => {
    assemblyTranscriptRef.current = assemblyTranscript;
  }, [assemblyTranscript]);

  useEffect(() => {
    partialTranscriptRef.current = partialTranscript;
  }, [partialTranscript]);

  const waitForAssemblyClear = (timeoutMs = TIMEOUTS.ASSEMBLY_CLEAR_MS) => {
    if (
      !assemblyTranscriptRef.current?.trim() &&
      !partialTranscriptRef.current?.trim()
    ) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const start = Date.now();
      const interval = setInterval(() => {
        const cleared =
          !assemblyTranscriptRef.current?.trim() &&
          !partialTranscriptRef.current?.trim();

        if (cleared) {
          clearInterval(interval);
          resolve(true);
          return;
        }

        if (Date.now() - start >= timeoutMs) {
          clearInterval(interval);
          resolve(false);
        }
      }, 50);
    });
  };

  // Detect unsupported browsers and disable the control with a tooltip
  useEffect(() => {
    if (typeof window === "undefined") return;

    const issues = [];
    const AudioContextConstructor =
      window.AudioContext || window.webkitAudioContext;

    if (!navigator?.mediaDevices?.getUserMedia) {
      issues.push("microphone access");
    }
    if (typeof MediaRecorder === "undefined") {
      issues.push("recording");
    }
    if (!AudioContextConstructor) {
      issues.push("audio processing");
    }
    if (!("AudioWorkletNode" in window)) {
      issues.push("audio worklets");
    }

    if (issues.length) {
      setIsBrowserSupported(false);
      setUnsupportedReason(
        "Microphone dictation is not supported in this browser. Please use the latest Chrome, Edge, or Firefox."
      );
    }
  }, []);

  // Display assemblyError when it occurs
  useEffect(() => {
    if (assemblyError) {
      toast({
        title: "Transcription Error",
        description: assemblyError,
        variant: "destructive",
      });
    }
  }, [assemblyError, toast]);

  const {
    microphoneOpen,
    stopMicrophone,
    startMicrophone,
    queue,
    removeBlob,
    microphoneDeviceName,
    stream,
  } = useMicrophone();

  const queueRef = useRef(queue);
  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  const microphoneOpenRef = useRef(microphoneOpen);
  useEffect(() => {
    microphoneOpenRef.current = microphoneOpen;
  }, [microphoneOpen]);

  const clearPendingAudioQueue = useCallback(
    (max = 300) => {
      const currentQueue = queueRef.current;
      if (!Array.isArray(currentQueue) || currentQueue.length === 0) return;

      const toRemove = Math.min(currentQueue.length, max);
      for (let i = 0; i < toRemove; i++) {
        try {
          removeBlob();
        } catch (e) {
          break;
        }
      }
    },
    [removeBlob]
  );

  const waitForQueueEmpty = (timeoutMs = TIMEOUTS.QUEUE_EMPTY_DEFAULT_MS) => {
    const currentQueue = queueRef.current;
    if (!Array.isArray(currentQueue) || currentQueue.length === 0) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const start = Date.now();
      const interval = setInterval(() => {
        const q = queueRef.current;
        const empty = !Array.isArray(q) || q.length === 0;

        if (empty) {
          clearInterval(interval);
          resolve(true);
          return;
        }

        if (Date.now() - start >= timeoutMs) {
          clearInterval(interval);
          resolve(false);
        }
      }, 25);
    });
  };

  const microphoneDeviceNameRef = useRef(microphoneDeviceName);
  useEffect(() => {
    microphoneDeviceNameRef.current = microphoneDeviceName;
  }, [microphoneDeviceName]);

  // Update refs when props change
  useEffect(() => {
    onTranscriptionCompleteRef.current = onTranscriptionComplete;
  }, [onTranscriptionComplete]);

  useEffect(() => {
    onPartialTranscriptionRef.current = onPartialTranscription;
  }, [onPartialTranscription]);

  // Persist mic selection to localStorage
  useEffect(() => {
    if (typeof window !== "undefined" && selectedMicDeviceId) {
      localStorage.setItem(MICROPHONE_STORAGE_KEY, selectedMicDeviceId);
    }
  }, [selectedMicDeviceId]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !selectedMicDeviceId ||
      !navigator?.mediaDevices?.enumerateDevices
    ) {
      return;
    }

    let cancelled = false;

    const validateSelectedDevice = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) {
          return;
        }

        const audioInputs = devices.filter((device) => device.kind === "audioinput");
        const hasComparableIds = audioInputs.some((device) => Boolean(device.deviceId));

        if (!hasComparableIds) {
          return;
        }

        const selectedDeviceStillExists = audioInputs.some(
          (device) => device.deviceId === selectedMicDeviceId
        );

        if (!selectedDeviceStillExists) {
          localStorage.removeItem(MICROPHONE_STORAGE_KEY);
          setSelectedMicDeviceId(null);
        }
      } catch (error) {
        console.warn("[VoiceCommentInput] Unable to validate saved microphone:", error);
      }
    };

    validateSelectedDevice();

    return () => {
      cancelled = true;
    };
  }, [selectedMicDeviceId]);

  // Set up audio level detection when recording starts
  useEffect(() => {
    if (isRecording && stream) {
      try {
        // Create audio context and analyser
        const AudioContextConstructor =
          window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioContextConstructor();

        // Some browsers start AudioContext suspended; resume for reliable visualization.
        if (audioContextRef.current.state === "suspended") {
          audioContextRef.current.resume().catch(() => {});
        }

        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;

        const source = audioContextRef.current.createMediaStreamSource(stream);
        source.connect(analyserRef.current);

        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

        // Function to update audio level
        const updateLevel = () => {
          if (analyserRef.current && isRecording) {
            analyserRef.current.getByteFrequencyData(dataArray);

            // Calculate average volume
            const average =
              dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
            // Normalize to 0-1 range and amplify significantly for better visual effect
            // Multiply by 3 to make it more responsive to speech
            const normalizedLevel = Math.min(1, (average / 128) * 3);

            setAudioLevel(normalizedLevel);
            animationFrameRef.current = requestAnimationFrame(updateLevel);
          }
        };

        updateLevel();
      } catch (error) {
        console.error(
          "[VoiceCommentInput] Error setting up audio analysis:",
          error
        );
      }
    }

    // Cleanup on unmount or when recording stops
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close();
      }
      setAudioLevel(0);
    };
  }, [isRecording, stream]);

  const waitForConnectionReady = (timeoutMs = TIMEOUTS.CONNECTION_READY_MS) => {
    if (connectionReadyRef.current) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const start = Date.now();
      const interval = setInterval(() => {
        if (connectionReadyRef.current) {
          clearInterval(interval);
          resolve(true);
        } else if (Date.now() - start >= timeoutMs) {
          clearInterval(interval);
          resolve(false);
        }
      }, 150);
    });
  };

  const waitForMicrophoneOpen = (timeoutMs = TIMEOUTS.MIC_OPEN_MS) => {
    if (microphoneOpenRef.current) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const start = Date.now();
      const interval = setInterval(() => {
        if (microphoneOpenRef.current) {
          clearInterval(interval);
          resolve(true);
        } else if (Date.now() - start >= timeoutMs) {
          clearInterval(interval);
          resolve(false);
        }
      }, 50);
    });
  };

  const waitForMicrophoneClosed = (timeoutMs = TIMEOUTS.MIC_CLOSED_MS) => {
    if (!microphoneOpenRef.current) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const start = Date.now();
      const interval = setInterval(() => {
        if (!microphoneOpenRef.current) {
          clearInterval(interval);
          resolve(true);
        } else if (Date.now() - start >= timeoutMs) {
          clearInterval(interval);
          resolve(false);
        }
      }, 50);
    });
  };

  const waitForMicrophoneName = (timeoutMs = TIMEOUTS.MIC_NAME_MS) => {
    if (microphoneDeviceNameRef.current) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const start = Date.now();
      const interval = setInterval(() => {
        if (microphoneDeviceNameRef.current) {
          clearInterval(interval);
          resolve(true);
        } else if (Date.now() - start >= timeoutMs) {
          clearInterval(interval);
          resolve(false);
        }
      }, 50);
    });
  };

  const switchMicrophone = async (deviceId) => {
    setSelectedMicDeviceId(deviceId);

    // If not recording, it will apply next time.
    if (!isRecording) return;

    setIsSwitchingMic(true);
    try {
      setPartialText("");
      setAccumulatedTranscript("");
      lastProcessedTranscriptRef.current = "";

      try {
        stopMicrophone();
      } catch (e) {
        console.warn("[VoiceCommentInput] stopMicrophone failed:", e);
      }
      await waitForMicrophoneClosed();

      resetAssembly();
      await waitForAssemblyClear();

      const microphoneResult = await startMicrophone(false, deviceId);
      if (microphoneResult?.deviceId) {
        setSelectedMicDeviceId(microphoneResult.deviceId);
      }
      const micReady = await waitForMicrophoneOpen();
      if (!micReady) {
        throw new Error(
          "Microphone did not start after switching. Please try again."
        );
      }

      await waitForMicrophoneName();
    } catch (error) {
      console.error("[VoiceCommentInput] switchMicrophone failed:", error);
      toast({
        title: "Microphone Error",
        description:
          error.message ||
          "Unable to switch microphone. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSwitchingMic(false);
    }
  };

  const connectToAssemblyIfNeeded = async () => {
    // Do not trust connectionReady alone (it can become stale across reloads/errors).
    // Attempting connect() is safe because the provider guards against duplicate sockets.
    if (!connecting) {
      try {
        await connect({ mode: "manual" });
      } catch (e) {
        // connect() is expected to handle errors internally; keep this defensive.
      }
    }

    if (connectionReadyRef.current) {
      return true;
    }

    setManualConnectPending(true);
    try {
      const ready = await waitForConnectionReady();
      if (!ready) {
        const errorMsg =
          "Unable to connect to transcription service. Please check your internet connection and try again.";
        toast({
          title: "Connection Timeout",
          description: errorMsg,
          variant: "destructive",
        });
        throw new Error(errorMsg);
      }

      return true;
    } catch (error) {
      console.error(
        "VoiceCommentInput: Unable to establish Assembly connection:",
        error
      );

      // Show user-friendly error message if not already shown
      if (!error.message?.includes("Unable to connect to transcription service")) {
        toast({
          title: "Connection Failed",
          description:
            "Could not connect to transcription service. Please try again in a moment.",
          variant: "destructive",
        });
      }

      return false;
    } finally {
      setManualConnectPending(false);
    }
  };

  // Process audio queue
  useEffect(() => {
    const processAudioQueue = async () => {
      // IMPORTANT: Microphone + queue are shared across the page. DemEditor renders
      // multiple VoiceCommentInput instances, so ONLY the active session instance
      // should drain/send the queue.
      //
      // We allow draining while `isStopping` so the last chunk isn't dropped.
      const sessionActive = isRecording || isStopping;
      if (queue.length > 0 && connectionReady && sessionActive) {
        try {
          const audioData = queue[0];
          processedAudioChunkCountRef.current += 1;
          const didSend = await sendAudio(audioData);
          if (didSend) {
            removeBlob();
          }
        } catch (error) {
          console.error("VoiceCommentInput: Error processing audio:", error);
        }
      }
    };

    processAudioQueue();
  }, [queue, connectionReady, isRecording, isStopping, sendAudio, removeBlob]);

  // (debug logging removed)

  // Handle partial transcripts for live preview
  useEffect(() => {
    if (isRecording && partialTranscript && partialTranscript.trim() !== "") {
      setPartialText(partialTranscript);
      // Send partial transcription to parent to show in textarea
      if (typeof onPartialTranscriptionRef.current === "function") {
        onPartialTranscriptionRef.current(partialTranscript, isRecording);
      }
      // Partial transcripts are very frequent, commenting out to reduce noise
    } else if (!isRecording) {
      setPartialText("");
      // Clear partial transcription in parent
      if (typeof onPartialTranscriptionRef.current === "function") {
        onPartialTranscriptionRef.current("", isRecording);
      }
    }
  }, [partialTranscript, isRecording]);

  // Handle final transcripts - accumulate them during recording session
  useEffect(() => {
    if (assemblyTranscript && assemblyTranscript.trim() !== "" && isRecording) {
      // Normalize both transcripts for comparison (remove punctuation and lowercase)
      const normalizeText = (text) =>
        text.toLowerCase().replace(/[.,!?;:]/g, "").trim();
      const normalizedNew = normalizeText(assemblyTranscript);
      const normalizedLast = normalizeText(lastProcessedTranscriptRef.current);

      // Check if this is a duplicate/formatted version of the last transcript
      if (normalizedNew === normalizedLast) {
        resetAssembly();
        return;
      }

      // This is a new transcript, accumulate it
      setAccumulatedTranscript((prev) => {
        return prev ? `${prev} ${assemblyTranscript}` : assemblyTranscript;
      });

      // Remember this transcript for duplicate detection
      lastProcessedTranscriptRef.current = assemblyTranscript;

      // Clear partial text since it's now part of the accumulated text
      setPartialText("");

      // Reset the assembly transcript so we don't process it again
      resetAssembly();
    }
  }, [assemblyTranscript, isRecording, resetAssembly]);

  const startRecording = async () => {
    setIsStarting(true);
    try {
      // If we are already recording, no-op.
      if (isRecording) return;

      // If a previous stop is still finalizing/tearing down, wait for it.
      // Without this, a late final transcript from the prior field can land
      // while the next field is recording and get appended to the wrong input.
      if (stopPromiseRef.current) {
        try {
          await stopPromiseRef.current;
        } catch (e) {
          // ignore
        }
      }

      // Defensive: clear any queued audio from a previous field/session so it cannot
      // be sent as part of this new recording.
      clearPendingAudioQueue();

      // If a prior session left the microphone "open" (or state is stale),
      // force a cleanup so we don't show the modal with a dead mic.
      if (microphoneOpenRef.current) {
        try {
          stopMicrophone();
        } catch (e) {
          console.warn("[VoiceCommentInput] stopMicrophone failed:", e);
        }
        await waitForMicrophoneClosed();
      }

      const ready = await connectToAssemblyIfNeeded();
      if (!ready) {
        setIsStarting(false);
        return;
      }

      // Clear all transcript state when starting new recording
      setPartialText("");
      setAccumulatedTranscript("");
      lastProcessedTranscriptRef.current = "";

      // Clear shared Assembly context transcripts BEFORE we flip isRecording true.
      // Without waiting for context state to clear, a newly-focused VoiceCommentInput
      // can accidentally consume the previous field's final transcript.
      resetAssembly();
      await waitForAssemblyClear();

      // Clear again after reset (defensive): ensures no stale audio is sent on a fresh session.
      clearPendingAudioQueue();

      const microphoneResult = await startMicrophone(
        false,
        selectedMicDeviceId
      );
      processedAudioChunkCountRef.current = 0;
      if (microphoneResult?.deviceId) {
        setSelectedMicDeviceId(microphoneResult.deviceId);
      }

      // Ensure the microphone actually opened before we show the modal.
      const micReady = await waitForMicrophoneOpen();
      if (!micReady) {
        throw new Error(
          "Microphone did not start. Please try again or refresh the page."
        );
      }

      // Best-effort: wait for a device name so the modal can show which mic is active.
      await waitForMicrophoneName();

      setIsRecording(true);
    } catch (error) {
      console.error("VoiceCommentInput: Error starting microphone:", error);

      // Provide user-friendly error messages for common microphone issues
      let errorMessage = "Unable to access microphone. Please try again.";

      if (
        error.name === "NotAllowedError" ||
        error.name === "PermissionDeniedError"
      ) {
        errorMessage =
          "Microphone access denied. Please enable microphone permissions in your browser settings.";
      } else if (error.name === "NotFoundError") {
        errorMessage =
          "No microphone detected. Please check that your microphone is connected.";
      } else if (error.name === "NotReadableError") {
        errorMessage =
          "Microphone is already in use by another application. Please close other apps using your microphone.";
      } else if (
        error.name === "OverconstrainedError" ||
        error.name === "ConstraintNotSatisfiedError"
      ) {
        errorMessage =
          "The selected microphone is unavailable or unsupported right now. Try again or choose a different microphone.";
      } else if (isMicrophoneBusyError(error)) {
        errorMessage =
          "Microphone is still starting. Please wait a moment and try again.";
      } else if (error.name === "NotSupportedError") {
        errorMessage =
          "Microphone dictation is not supported in this browser. Please use the latest Chrome, Edge, or Firefox.";
      }

      toast({
        title: "Microphone Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsStarting(false);
    }
  };

  const stopRecording = () => {
    if (isStopping) return;

    // Always allow the modal to be dismissed immediately, even if mic failed.
    setIsRecording(false);
    setIsStopping(true);

    const stopPromise = (async () => {
      try {
        // Clear partial transcription from parent textarea
        if (typeof onPartialTranscriptionRef.current === "function") {
          onPartialTranscriptionRef.current("", false);
        }

        // Give the queue a short window to drain BEFORE we shut down the mic.
        // Otherwise the last 50ms chunk (often containing the last word) can be dropped.
        await new Promise((resolve) =>
          setTimeout(resolve, TIMEOUTS.PRE_STOP_QUEUE_DRAIN_DELAY_MS)
        );
        await waitForQueueEmpty(TIMEOUTS.PRE_STOP_QUEUE_DRAIN_TIMEOUT_MS);

        try {
          if (typeof stopMicrophone === "function") {
            // Preserve queued audio so we can finish sending it while finalizing.
            stopMicrophone({ clearQueue: false });
          }
        } catch (e) {
          console.warn("[VoiceCommentInput] stopMicrophone failed:", e);
        }

        // Continue draining any remaining queued audio after mic shutdown.
        await waitForQueueEmpty(TIMEOUTS.POST_STOP_QUEUE_DRAIN_TIMEOUT_MS);

        // Force end-of-turn so the server finalizes the last word immediately.
        // This avoids relying on long silence-based turn detection in form mode.
        try {
          forceEndpoint?.();
        } catch (e) {
          // ignore
        }

        // Give Assembly a short window to send a final transcript for the last turn.
        await new Promise((resolve) =>
          setTimeout(resolve, TIMEOUTS.POST_FORCE_ENDPOINT_WAIT_MS)
        );

        // Prefer a final transcript if one arrived after we stopped; otherwise use partial.
        const tail =
          (assemblyTranscriptRef.current || "").trim() ||
          (partialTranscriptRef.current || "").trim();

        const completeTranscript =
          accumulatedTranscript && tail
            ? `${accumulatedTranscript} ${tail}`.trim()
            : (accumulatedTranscript || tail || "").trim();

        if (
          completeTranscript &&
          typeof onTranscriptionCompleteRef.current === "function"
        ) {
          onTranscriptionCompleteRef.current(completeTranscript);
        }

        // Clear transcripts and tear down the websocket so late messages from the
        // previous field cannot appear in the next field's recording session.
        resetAssembly();
        try {
          await disconnect();
        } catch (e) {
          // ignore
        }

        // Ensure no stale audio remains for the next field/session.
        clearPendingAudioQueue();
      } catch (e) {
        // stopRecording() can be triggered from UI events without awaiting.
        // Ensure we never create an unhandled rejection.
        console.error("[VoiceCommentInput] stopRecording error:", e);
      } finally {
        setPartialText("");
        setAccumulatedTranscript("");
        lastProcessedTranscriptRef.current = "";
        setIsStopping(false);
        stopPromiseRef.current = null;
      }
    })();

    stopPromiseRef.current = stopPromise;
    return stopPromise;
  };

  // Modal dismissal helpers
  useEffect(() => {
    if (!isRecording) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        stopRecording();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isRecording]);

  const isConnecting =
    isStarting ||
    manualConnectPending ||
    isSwitchingMic ||
    isStopping ||
    (!connectionReady && connecting);

  const renderIcon = () => {
    if (isConnecting) {
      return <Loader2 className="w-4 h-4 animate-spin" />;
    }

    const micIcon = isRecording ? (
      <MicOff className="w-4 h-4" />
    ) : (
      <Mic className="w-4 h-4" />
    );

    if (showDropdownIndicator) {
      return (
        <div className="flex items-center gap-1">
          {micIcon}
          <ChevronDown className="w-3 h-3" />
        </div>
      );
    }

    return micIcon;
  };

  const getButtonText = () => {
    if (isConnecting) return "Connecting...";
    if (isRecording) return "Stop";
    return "Speak instead";
  };

  return (
    <>
      <div className="flex flex-col items-end gap-2">
        <Button
          type="button"
          aria-label={getButtonText()}
          data-testid="voice-comment-input-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            isRecording ? stopRecording() : startRecording();
          }}
          variant="outline"
          size={showDropdownIndicator ? "sm" : "sm"}
          disabled={disabled || isConnecting || !isBrowserSupported}
          title={!isBrowserSupported ? unsupportedReason : getButtonText()}
          className={`flex-shrink-0 transition-all border font-medium ${
            isConnecting
              ? "text-gray-500 cursor-wait"
              : "border-slate-300 text-slate-600 hover:border-slate-400 hover:bg-slate-100 hover:text-slate-700"
          }`}
        >
          {showDropdownIndicator ? (
            <span className="inline-flex items-center">{renderIcon()}</span>
          ) : (
            <>
              <span
                className={`${compact ? "inline-flex" : "inline-flex sm:hidden"}`}
              >
                {renderIcon()}
              </span>
              <span
                className={`${compact ? "hidden" : "hidden sm:inline-flex"} items-center gap-2 px-3 h-10`}
              >
                {renderIcon()}
                <span>{getButtonText()}</span>
              </span>
            </>
          )}
        </Button>

        {!isRecording && !compact && !showDropdownIndicator && !hideMicSelector && (
          <div className="hidden sm:block">
            <MicrophoneSelector
              onMicrophoneSelect={setSelectedMicDeviceId}
              selectedDeviceId={selectedMicDeviceId}
            />
          </div>
        )}
      </div>

      {isRecording &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] animate-in fade-in duration-300"
            style={{ pointerEvents: "auto" }}
            onMouseDown={(e) => {
              // Click outside closes
              if (e.target === e.currentTarget) {
                stopRecording();
              }
            }}
          >
            <div
              className="bg-white rounded-3xl shadow-2xl p-12 max-w-md mx-4 text-center"
              style={{ pointerEvents: "auto" }}
              onMouseDown={(e) => {
                // Prevent backdrop click handler
                e.stopPropagation();
              }}
            >
              <div className="relative inline-flex items-center justify-center mb-6">
                <div className="absolute w-32 h-32 bg-action-500/20 rounded-full animate-ping" />
                <div
                  className="absolute w-24 h-24 bg-action-500/30 rounded-full animate-ping"
                  style={{ animationDelay: "0.2s" }}
                />
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    stopRecording();
                  }}
                  className="relative w-20 h-20 bg-gradient-to-br from-action-500 to-action-600 rounded-full flex items-center justify-center shadow-xl hover:shadow-2xl hover:scale-105 transition-transform cursor-pointer"
                  style={{ pointerEvents: "auto" }}
                >
                  <MicOff className="w-10 h-10 text-white" />
                </button>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Listening...
              </h3>
              <p className="text-sm text-gray-500 mb-6 flex items-center justify-center gap-2">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                  />
                </svg>
                {microphoneDeviceName || "Microphone"}
              </p>

              {!connectionReady && (
                <p className="text-sm text-gray-500 mb-6 flex items-center justify-center gap-2">
                  Connecting to transcription…
                </p>
              )}

              {/* In compact mode (like DEM editor), the selector is hidden outside the modal.
                  Provide it here so users can recover if a non-mic device is selected. */}
              {!showDropdownIndicator && (
                <div className="mb-6 flex justify-center">
                  <MicrophoneSelector
                    onMicrophoneSelect={switchMicrophone}
                    selectedDeviceId={selectedMicDeviceId}
                  />
                </div>
              )}

              {isSwitchingMic && (
                <p className="text-xs text-gray-500 mb-4">
                  Switching microphone…
                </p>
              )}

              <div className="flex items-center justify-center gap-1 h-12 mb-6">
                {[...Array(5)].map((_, i) => {
                  const baseHeight = 10;
                  const maxHeight = 90;
                  const variation = 0.7 + (i % 2) * 0.3;
                  const height =
                    baseHeight +
                    (maxHeight - baseHeight) * audioLevel * variation;

                  return (
                    <div
                      key={i}
                      className="w-1.5 bg-action-500 rounded-full transition-all duration-150 ease-out"
                      style={{
                        height: `${Math.max(8, height)}%`,
                      }}
                    />
                  );
                })}
              </div>

              {(accumulatedTranscript || partialText) && (
                <div className="mb-6 p-4 bg-action-50 rounded-xl border border-action-200 max-h-32 overflow-y-auto">
                  <p className="text-sm text-gray-700">
                    {accumulatedTranscript && <span>{accumulatedTranscript}</span>}
                    {accumulatedTranscript && partialText && <span> </span>}
                    {partialText && <span className="italic">{partialText}</span>}
                  </p>
                </div>
              )}

              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  stopRecording();
                }}
                className="w-full py-3 px-6 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900 font-semibold rounded-xl transition-colors cursor-pointer"
                style={{ pointerEvents: "auto" }}
              >
                Stop (& edit text if you wish)
              </button>

              <p className="text-xs text-gray-400 mt-4">
                Click the microphone or this button to stop
              </p>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default VoiceCommentInput;
